import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useAutoLogout } from "./useAutoLogout";
import LandingPage from "./LandingPage";
import LoginPage from "./LoginPage";
import Dashboard from "./Dashboard";
import CheckoutPage from "./CheckoutPage";
import OrdersPage from "./OrdersPage";
import OrderDetailPage from "./OrderDetailPage";
import SellerDashboardPage from "./SellerDashboardPage";
import DisputePage from "./DisputePage";
import DisputeTrackPage from "./DisputeTrackPage";
import DeliveryPage from "./DeliveryPage";
import HomePage from "./HomePage";
import ProfilePage from "./ProfilePage";
import ListingPage from "./ListingPage";
import ListingDetailPage from "./ListingDetailPage";
import MessagesPage from "./MessagesPage";
import ChatPage from "./ChatPage";
import InvoicesPage from "./InvoicesPage";
import InvoicePayPage from "./InvoicePayPage";
import InvoiceTrackPage from "./InvoiceTrackPage";
import LegalPage from "./LegalPage";
import PrivacyPage from "./PrivacyPage";
import TermsPage from "./TermsPage";
import RefundPolicyPage from "./RefundPolicyPage";
import AuctionsPage from "./AuctionsPage";

function AppInner({ user, setUser }) {
  function handleLogin(userData) { setUser(userData); }
  function handleLogout() {
    localStorage.removeItem("itrust_token");
    localStorage.removeItem("itrust_user");
    setUser(null);
  }

  useAutoLogout(user, handleLogout);

  return (
    <Routes>
      <Route path="/" element={<LandingPage user={user} onLogout={handleLogout} />} />
      <Route path="/login" element={user ? <Navigate to="/home" replace /> : <LoginPage onLogin={handleLogin} />} />
      <Route path="/home" element={user ? <HomePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/orders" element={user ? <OrdersPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/orders/:orderId" element={user ? <OrderDetailPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/seller" element={user ? <SellerDashboardPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/profile" element={user ? <ProfilePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/listing/:id" element={user ? <ListingPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/dispute/:orderId" element={user ? <DisputePage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/dispute/track/:orderId" element={user ? <DisputeTrackPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/messages" element={user ? <MessagesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/messages/:id" element={user ? <ChatPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/delivery/:orderId" element={user ? <DeliveryPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/listing-detail/:id" element={user ? <ListingDetailPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/invoices" element={user ? <InvoicesPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/invoice/:slug" element={<InvoicePayPage user={user} />} />
      <Route path="/invoice/track/:token" element={<InvoiceTrackPage />} />
      <Route path="/auctions" element={user ? <AuctionsPage user={user} onLogout={handleLogout} /> : <Navigate to="/login" replace />} />
      <Route path="/legal" element={<LegalPage user={user} onLogout={handleLogout} />} />
      <Route path="/legal/privacy" element={<PrivacyPage user={user} onLogout={handleLogout} />} />
      <Route path="/legal/terms" element={<TermsPage user={user} onLogout={handleLogout} />} />
      <Route path="/legal/refunds" element={<RefundPolicyPage user={user} onLogout={handleLogout} />} />
      <Route path="/checkout/complete" element={
        <div style={{ background: "#eef2fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, fontFamily: "Arial, sans-serif" }}>
          <div style={{ fontSize: 64 }}>🎉</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 800 }}>Payment Complete!</div>
          <div style={{ color: "#64748b", fontSize: 16, textAlign: "center", maxWidth: 400 }}>Your payment was successful.</div>
          <a href="/orders" style={{ color: "#3b82f6", fontSize: 16, fontWeight: 700 }}>Go to My Orders →</a>
        </div>
      } />
      <Route path="/seller/connect/complete" element={
        <div style={{ background: "#eef2fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, fontFamily: "Arial, sans-serif" }}>
          <div style={{ fontSize: 64 }}>✅</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 800 }}>Payment Account Connected!</div>
          <a href="/seller" style={{ color: "#3b82f6", fontSize: 16, fontWeight: 700 }}>Go to Seller Dashboard →</a>
        </div>
      } />
      <Route path="/seller/connect/refresh" element={
        <div style={{ background: "#eef2fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: 16, fontFamily: "Arial, sans-serif" }}>
          <div style={{ fontSize: 64 }}>🔄</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 800 }}>Session Expired</div>
          <a href="/seller" style={{ color: "#3b82f6", fontSize: 16, fontWeight: 700 }}>Go to Seller Dashboard →</a>
        </div>
      } />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    const stored = localStorage.getItem("itrust_user");
    const token = localStorage.getItem("itrust_token");
    if (stored && token) {
      try { setUser(JSON.parse(stored)); }
      catch {
        localStorage.removeItem("itrust_user");
        localStorage.removeItem("itrust_token");
      }
    }
    setAuthChecked(true);
  }, []);

  if (!authChecked) {
    return (
      <div style={{ background: "#eef2fb", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", color: "#0f172a", fontFamily: "Arial, sans-serif" }}>
        Loading...
      </div>
    );
  }

  return (
    <BrowserRouter>
      <AppInner user={user} setUser={setUser} />
    </BrowserRouter>
  );
}

export default App;